import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { 
  Megaphone, 
  Plus, 
  Edit2, 
  Trash2, 
  AlertCircle,
  CheckCircle,
  Clock
} from "lucide-react";

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    active: true,
    priority: "normal"
  });

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/announcements`, {
        withCredentials: true
      });
      setAnnouncements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load announcements");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingAnnouncement) {
        await axios.put(
          `${API}/admin/announcements/${editingAnnouncement.announcement_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success("Announcement updated");
      } else {
        await axios.post(
          `${API}/admin/announcements`,
          formData,
          { withCredentials: true }
        );
        toast.success("Announcement created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch {
      toast.error("Failed to save announcement");
    }
  };

  const handleDelete = async (announcementId) => {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    
    try {
      await axios.delete(
        `${API}/admin/announcements/${announcementId}`,
        { withCredentials: true }
      );
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch {
      toast.error("Failed to delete announcement");
    }
  };

  const handleToggleActive = async (announcement) => {
    try {
      await axios.put(
        `${API}/admin/announcements/${announcement.announcement_id}`,
        { active: !announcement.active },
        { withCredentials: true }
      );
      toast.success(`Announcement ${!announcement.active ? 'activated' : 'deactivated'}`);
      fetchAnnouncements();
    } catch {
      toast.error("Failed to update announcement");
    }
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      active: announcement.active,
      priority: announcement.priority || "normal"
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: "",
      message: "",
      active: true,
      priority: "normal"
    });
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: "bg-red-100 text-red-700 border-red-200",
      normal: "bg-blue-100 text-blue-700 border-blue-200",
      low: "bg-gray-100 text-gray-600 border-gray-200"
    };
    return styles[priority] || styles.normal;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="h-40 bg-muted rounded-xl" />
            <div className="h-40 bg-muted rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <section className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="announcements-header">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#a69373' }}>Announcements</h1>
            <p className="text-white/70">Broadcast messages to all users.</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-announcement-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#f5f9f5] border-[#c5d5c5] max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-foreground">
                  {editingAnnouncement ? "Edit Announcement" : "Create Announcement"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label className="text-foreground/70">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Announcement title"
                    className="bg-white border-[#c5d5c5]"
                    data-testid="announcement-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground/70">Message *</Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your announcement message..."
                    className="bg-white border-[#c5d5c5] min-h-[120px]"
                    data-testid="announcement-message-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground/70">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger className="bg-white border-[#c5d5c5]" data-testid="announcement-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <Label className="text-foreground/70">Active</Label>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                    data-testid="announcement-active-switch"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="flex-1 border-[#c5d5c5] text-[#1a1a1a]">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button type="submit" className="flex-1 btn-primary" data-testid="save-announcement-btn">
                    {editingAnnouncement ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-3 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-heading text-white">{announcements.length}</div>
              <div className="text-sm text-white/60">Total</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-heading text-white">
                {announcements.filter(a => a.active).length}
              </div>
              <div className="text-sm text-white/60">Active</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-heading text-red-400">
                {announcements.filter(a => a.priority === "high" && a.active).length}
              </div>
              <div className="text-sm text-white/60">High Priority</div>
            </CardContent>
          </Card>
        </section>

        {/* Announcements List */}
        <section className="space-y-4">
          {announcements.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <Megaphone className="w-12 h-12 text-foreground/20 mx-auto mb-4" />
                <h3 className="text-foreground font-medium mb-2">No announcements yet</h3>
                <p className="text-muted-foreground text-sm">
                  Create your first announcement to communicate with all users.
                </p>
              </CardContent>
            </Card>
          ) : (
            announcements.map((announcement) => (
              <Card 
                key={announcement.announcement_id} 
                className={`glass-card ${!announcement.active ? 'opacity-60' : ''}`}
                data-testid={`announcement-${announcement.announcement_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {announcement.active ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-400" />
                        )}
                        <h3 className="text-foreground font-medium">{announcement.title}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${getPriorityBadge(announcement.priority)}`}>
                          {announcement.priority || "normal"}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3">{announcement.message}</p>
                      <p className="text-xs text-foreground/40">
                        Created: {new Date(announcement.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={announcement.active}
                        onCheckedChange={() => handleToggleActive(announcement)}
                        data-testid={`toggle-${announcement.announcement_id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(announcement)}
                        className="text-foreground/60 hover:text-foreground"
                        data-testid={`edit-${announcement.announcement_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(announcement.announcement_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-${announcement.announcement_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </section>
      </main>
    </div>
  );
}
